import React from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import ParitySnapshot from '@app/model/parity/ParitySnapshot';
import DataProvider from '@app/model/_common/DataProvider';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import ParityDailyProviderSnapshot from '@app/model/parity/ParityDailyProviderSnapshot';
import Utilities from '@util/Utilities';
import CompanyHotelSettings from '@app/model/company_hotel/settings/CompanyHotelSettings';
import moment from 'moment/moment';
import { findProviderData } from '@app/component/provider/FindProviderData';
import {
	useMarketStateParityForPeriod,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateParityForPeriod';
import {
	useOtaPricesForPeriod,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useOtaPricesForPeriod';
import { OtaPriceStateSnapshot } from '@app/model/parity/OtaPriceStateSnapshot';
import { OtaPriceStateItem } from '@app/model/parity/OtaPriceStateItem';

const DashboardParityReportView: React.FunctionComponent<{
	companyHotel: CompanyHotel
}> = ({ companyHotel }) => {

	const otaPriceStateSnapshot = useOtaPricesForPeriod();
	const paritySnapshot = useMarketStateParityForPeriod();

	const activeProviders = [...new Set(companyHotel!.parsingSettings?.filter(s => s.state === 'ACTIVE' && s.variables.provider !== DataProvider.BRAND_COM).map(s => s.variables.provider) || [])].sort();

	let hasReport = false;

	if (!!companyHotel.settings && !!paritySnapshot && !_isEmpty(paritySnapshot.dailyItems)) {
		return <>
			{activeProviders.map(p => {

				const items = findProviderDiscrepanciesReport(paritySnapshot, p, companyHotel.settings);
				if (!_isEmpty(items)) {
					hasReport = true;
					return <div className="space-y-6 mt-4" key={`provider_report_item_${p}`}>
						<RenderProviderReport items={items} provider={p} />
					</div>;
				}
				return <div />;
			})}

			{(() => {
				if (!otaPriceStateSnapshot.isEmpty) {
					const items = findOtaDiscrepanciesReport(paritySnapshot, otaPriceStateSnapshot, companyHotel.settings);
					if (!_isEmpty(items)) {
						hasReport = true;
						return <div className="space-y-6 mt-4">
							<RenderOtaReport items={items} />
						</div>;
					}
				}
				return <div />;
			})()}

			{!hasReport && <Message state="info">
				There are no discrepancies found for the selected period!
			</Message>}
		</>;
	} else
		return <Message state="error">
			Parity report could not be loaded. Please try again later!
		</Message>;
};

const findProviderDiscrepanciesReport = (paritySnapshot: ParitySnapshot, provider: DataProvider, companyHotelSettings: CompanyHotelSettings): ProviderParityItem[] => {
	const items: ProviderParityItem[] = [];

	for (const dailyItem of paritySnapshot.dailyItems) {
		const brandItem = dailyItem.providerItems.find(pi => pi.provider === DataProvider.BRAND_COM);
		if (!!brandItem && brandItem.isAvailable)
			for (const providerItem of dailyItem.providerItems) {
				if (providerItem.provider === provider && providerItem.isAvailable && brandItem.item.price.amount > providerItem.item.price.amount * (1 + ((companyHotelSettings?.calendar?.parityDifferencePercentage ?? 0) / 100))) {
					items.push({
						statDate: dailyItem.statDate,
						brandItem: brandItem,
						providerItem: providerItem,
						difference: brandItem.item.price.amount - providerItem.item.price.amount,
					});
				}
			}
	}

	return items;
};

const findOtaDiscrepanciesReport = (paritySnapshot: ParitySnapshot, otaPriceStateSnapshot: OtaPriceStateSnapshot, companyHotelSettings: CompanyHotelSettings): ProviderParityItem[] => {
	const items: ProviderParityItem[] = [];

	for (const dailyItem of paritySnapshot.dailyItems) {
		const brandItem = dailyItem.providerItems.find(pi => pi.provider === DataProvider.BRAND_COM);
		if (!!brandItem && brandItem.isAvailable) {
			const otaPriceDailyState = otaPriceStateSnapshot.dailyItems.find(di => di.statDate === dailyItem.statDate);
			if (!!otaPriceDailyState && otaPriceDailyState.isAvailable && !otaPriceDailyState.isSoldOut) {
				const cheaperItem = otaPriceDailyState.items[0];
				if (brandItem.item.price.amount > cheaperItem.price.amount * (1 + ((companyHotelSettings?.calendar?.parityDifferencePercentage ?? 0) / 100)))
					items.push({
						statDate: dailyItem.statDate,
						brandItem: brandItem,
						otaItem: cheaperItem,
						difference: brandItem.item.price.amount - cheaperItem.price.amount,
					});
			}
		}
	}
	return items;
};

type ProviderParityItem = {
	statDate: string,
	brandItem: ParityDailyProviderSnapshot
	providerItem?: ParityDailyProviderSnapshot
	otaItem?: OtaPriceStateItem
	difference: number
}

const RenderProviderReport: React.FunctionComponent<{
	provider: DataProvider
	items: ProviderParityItem[],
}>
	= ({ provider, items }) => {


	const [isOpen, setIsOpen] = React.useState(true);


	const providerData = findProviderData(provider);

	const currency = items[0].brandItem.item.price.currency;
	const sum: number = items.map(item => item.difference).reduce((a, b) => a + b);
	const average = sum / items.length;

	const today = new Date();

	return <div className="bg-white shadow sm:overflow-hidden sm:rounded-lg">
		<div className="divide-y divide-gray-200">
			<div className="px-4 py-4 sm:px-6">
				<div className="flex items-center justify-between">
					<div className="flex flex-row flex-wrap justify-start gap-x-5 gap-y-2 items-center">
						<div className="flex justify-start">
							{!!providerData?.logo  ? providerData.logo() : <></>}
						</div>
						<div className="flex items-center text-sm text-gray-500">
							<CalendarDaysIcon className="mr-1 w-5 h-auto shrink-0 text-gray-400" />
							Total:<span className="ml-1">{items.length}</span>
						</div>
						<div className="flex items-center text-sm text-gray-500">
								<span
									className="flex w-4 h-4 rounded-full items-center justify-center ring-1 ring-gray-400 shrink-0 text-gray-400 text-xs pr-[1px]">{Utilities.formatCurrency(currency)}</span>
							<span className="ml-2">Avg. Undercut:</span>
							<span className="ml-1">{Utilities.formatDouble(average)}</span>
						</div>
					</div>
					<button className="btn p-1 hover:text-blue-500 hover:bg-gray-50" onClick={() => setIsOpen(!isOpen)}>
						{isOpen ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
					</button>
				</div>
			</div>
			{isOpen && <div className="block">
				<div className="overflow-x-auto overflow-y-auto max-h-[48dvh]">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-300">
							<thead className="bg-gray-50">
							<tr className="divide-x divide-gray-200">
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Hotel Rate</th>
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">OTA Rate</th>
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Difference</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
							{items.map((item, index) => {
									const diff = moment(item.statDate).diff(today, 'days');
									const isPast = diff < 0;
									const isToday = moment(item.statDate).isSame(today, 'day');

									return <tr key={`${provider}_report_item_${index}`}
														 className={Utilities.classNames('divide-x divide-gray-200', isPast ? 'opacity-60' : '', isToday ? 'bg-blue-50' : '')}>
										<td
											className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{Utilities.formatDateString(item.statDate, 'ddd DD/MM', false)}</td>
										<td
											className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{Utilities.formatPrice(item.brandItem.item.price)}</td>
										<td
											className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{Utilities.formatPrice(item.providerItem!.item.price)}</td>
										<td className="whitespace-nowrap px-3 py-4 text-sm"><span
											className="text-amber-500">-{Utilities.formatAmount(item.difference, currency)}</span>
										</td>
									</tr>;
								},
							)}
							</tbody>
						</table>
					</div>
				</div>
			</div>}
		</div>
	</div>;

};

const RenderOtaReport: React.FunctionComponent<{
	items: ProviderParityItem[]
}> = ({ items }) => {
	const [isOpen, setIsOpen] = React.useState(true);


	const currency = items[0].brandItem.item.price.currency;
	const sum: number = items.map(item => item.difference).reduce((a, b) => a + b);
	const average = sum / items.length;

	const today = new Date();

	return <div className="bg-white shadow sm:overflow-hidden sm:rounded-lg">
		<div className="divide-y divide-gray-200">
			<div className="px-4 py-4 sm:px-6">
				<div className="flex items-center justify-between">
					<div className="flex flex-row flex-wrap justify-start gap-x-5 gap-y-2 items-center">
						<div className="flex justify-start text-sm">
							Other OTAs
						</div>
						<div className="flex items-center text-sm text-gray-500">
							<CalendarDaysIcon className="mr-1 w-5 h-auto shrink-0 text-gray-400" />
							Total:<span className="ml-1">{items.length}</span>
						</div>
						<div className="flex items-center text-sm text-gray-500">
								<span
									className="flex w-4 h-4 rounded-full items-center justify-center ring-1 ring-gray-400 shrink-0 text-gray-400 text-xs pr-[1px]">{Utilities.formatCurrency(currency)}</span>
							<span className="ml-2">Avg. Undercut:</span>
							<span className="ml-1">{Utilities.formatDouble(average)}</span>
						</div>
					</div>
					<button className="btn p-1 hover:text-blue-500 hover:bg-gray-50" onClick={() => setIsOpen(!isOpen)}>
						{isOpen ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
					</button>
				</div>
			</div>
			{isOpen && <div className="block">
				<div className="overflow-x-auto overflow-y-auto max-h-[48dvh]">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-300">
							<thead className="bg-gray-50">
							<tr className="divide-x divide-gray-200">
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Hotel Rate</th>
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">OTA</th>
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">OTA Rate</th>
								<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Difference</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
							{items.map((item, index) => {
									const diff = moment(item.statDate).diff(today, 'days');
									const isPast = diff < 0;
									const isToday = moment(item.statDate).isSame(today, 'day');

									return <tr key={`ota_report_item_${index}`}
														 className={Utilities.classNames('divide-x divide-gray-200', isPast ? 'opacity-60' : '', isToday ? 'bg-blue-50' : '')}>
										<td
											className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{Utilities.formatDateString(item.statDate, 'ddd DD/MM', false)}</td>
										<td
											className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{Utilities.formatPrice(item.brandItem.item.price)}</td>
										<td
											className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
											<div className="flex items-center gap-x-2">
												<img className="block h-4 w-4"
														 src={`https://${item.otaItem!.iconUrl}`}
														 alt={item.otaItem!.provider}
														 title={item.otaItem!.provider} />
												<span>{item.otaItem!.provider}</span>
											</div>
										</td>
										<td
											className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{Utilities.formatPrice(item.otaItem!.price)}</td>
										<td className="whitespace-nowrap px-3 py-4 text-sm"><span
											className="text-amber-500">-{Utilities.formatAmount(item.difference, currency)}</span>
										</td>
									</tr>;
								},
							)}
							</tbody>
						</table>
					</div>
				</div>
			</div>}
		</div>
	</div>;

};

export default DashboardParityReportView;